<template>
  <div class="min-h-screen" data-theme="violet">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="competitions" />

    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a :href="'/competitions'" class="text-sm text-blue-700 hover:text-blue-900 mb-6 inline-block">&larr; {{ t('competitions.back_to_list') }}</a>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div class="flex items-center gap-3 mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {{ formatDate(comp.date) }}
          </span>
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
            {{ formatTime(comp.date) }}
          </span>
          <span v-if="comp.running_type" class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
            {{ t('competitions.type_' + comp.running_type) }}
          </span>
        </div>

        <div v-if="comp.logo_url" class="mb-4">
          <img :src="comp.logo_url" class="w-full rounded-lg object-contain border border-gray-200" />
        </div>
        <h1 class="text-2xl font-bold text-gray-900 mb-4">{{ comp.name }}</h1>

        <div v-if="comp.distances && comp.distances.length > 0" class="flex flex-wrap gap-2 mb-4">
          <span v-for="d in comp.distances" :key="d.id" class="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
            {{ d.km }} km
            <span v-if="d.elevation_gain" class="text-gray-400">↑{{ d.elevation_gain }}m</span>
          </span>
        </div>

        <div v-if="comp.pending && userAdmin" class="mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 mr-2">
            {{ t('competitions.pending') }}
          </span>
          <button @click="approveCompetition" class="px-4 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition">
            {{ t('competitions.approve') }}
          </button>
        </div>
        <div v-else-if="comp.pending" class="mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            {{ t('competitions.pending') }}
          </span>
        </div>

        <p v-if="comp.description" class="text-gray-600 mb-6 whitespace-pre-line">{{ comp.description }}</p>

        <div v-if="comp.link || comp.results_link" class="flex flex-wrap gap-2 mb-6">
          <a v-if="comp.link" :href="comp.link" target="_blank"
             class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition">
            {{ t('competitions.visit_link') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
          </a>
          <a v-if="comp.results_link" :href="comp.results_link" target="_blank"
             class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-700 rounded-lg hover:bg-green-800 transition">
            {{ t('competitions.results') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
          </a>
        </div>

        <div v-if="comp.latitude && comp.longitude" class="rounded-lg border border-gray-200 overflow-hidden map-container mb-6">
          <div ref="mapContainer" style="height: 300px; width: 100%;"></div>
        </div>

        <div v-if="userAdmin || comp.user_id === userId" class="flex gap-3 mt-4">
          <a :href="`/competitions?edit=${comp.id}`" class="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">{{ t('competitions.edit') }}</a>
          <button @click="deleteCompetition" class="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">
            {{ t('competitions.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

const props = defineProps({
  signedIn: Boolean,
  userName: String,
  userId: Number,
  userAdmin: Boolean,
  competition: Object,
  csrfToken: String
})

const { t, locale } = useI18n()

const comp = reactive({ ...props.competition })
const showMenu = ref(false)
const logoSize = ref(120)
const mapContainer = ref(null)

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) }
function formatTime(d) { return new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }

async function deleteCompetition() {
  if (!confirm(t('competitions.confirm_delete_message'))) return
  try {
    const res = await fetch(`/competitions/${comp.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) {
      window.location.href = '/competitions'
    }
  } catch {}
}

async function approveCompetition() {
  try {
    const res = await fetch(`/competitions/${comp.id}/approve`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) {
      comp.pending = false
    }
  } catch {}
}

onMounted(() => {
  if (comp.latitude && comp.longitude && mapContainer.value) {
    const map = L.map(mapContainer.value).setView([parseFloat(comp.latitude), parseFloat(comp.longitude)], 15)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map)
    L.marker([parseFloat(comp.latitude), parseFloat(comp.longitude)]).addTo(map)
    setTimeout(() => map.invalidateSize(), 100)
  }
})
</script>
